import { z } from "zod"

/**
 * SEO и PWA веб-витрины (fd-web-03, fd-web-04).
 *
 * `SeoPage` — мета-данные одной страницы витрины одним ответом
 * (`GET /seo/page?path=`): их читает сервер витрины и шлюз при отдаче HTML,
 * а SPA — при переходах (хук usePageMeta).
 */
export const SeoPageSchema = z.object({
  /** Запрошенный путь без хоста (с query, как пришёл). */
  path: z.string(),
  /** 200 — страница есть, 301 — переезд на `redirect`, 404 — такой страницы нет. */
  status: z.union([z.literal(200), z.literal(301), z.literal(404)]),
  /** Путь (с query) для 301: старый `/product/:id` → ЧПУ. */
  redirect: z.string().optional(),
  title: z.string(),
  description: z.string(),
  /** Абсолютный канонический адрес. */
  canonical: z.string(),
  /** Содержимое `<meta name="robots">`. */
  robots: z.string(),
  image: z.string().optional(),
  /** Готовые теги для `<head>`: title, description, canonical, OG/Twitter, JSON-LD. */
  headHtml: z.string(),
  /** Содержимое страницы без JS (для поисковых роботов). */
  bodyHtml: z.string(),
  jsonLd: z.array(z.record(z.string(), z.unknown())),
})
export type SeoPage = z.infer<typeof SeoPageSchema>

/** Подписка браузера на Web Push (PushSubscription.toJSON()). */
export const WebPushSubscriptionInputSchema = z.object({
  endpoint: z.string().url().max(2000),
  keys: z.object({
    p256dh: z.string().min(16).max(200),
    auth: z.string().min(8).max(100),
  }),
})
export type WebPushSubscriptionInput = z.infer<typeof WebPushSubscriptionInputSchema>

export const WebPushUnsubscribeInputSchema = z.object({
  endpoint: z.string().url().max(2000),
})
export type WebPushUnsubscribeInput = z.infer<typeof WebPushUnsubscribeInputSchema>

/** Открытый VAPID-ключ для `pushManager.subscribe`. `enabled=false` — канал на стенде не настроен. */
export const WebPushConfigSchema = z.object({
  enabled: z.boolean(),
  publicKey: z.string(),
})
export type WebPushConfig = z.infer<typeof WebPushConfigSchema>
